import ReactECharts from 'echarts-for-react';

// Metadata columns that are not value columns
const META_COLS = new Set(['row_type', 'category', 'depth', 'label']);

// Color palette for KPI series
const KPI_COLORS = {
  'Gross Profit': '#10b981',       // emerald-500
  'Net Operating Income': '#3b82f6', // blue-500
  'Net Other Income': '#f59e0b',    // amber-500
  'Net Income': '#8b5cf6',         // violet-500
};
const FALLBACK_COLORS = ['#06b6d4', '#ec4899', '#f97316', '#14b8a6'];

export default function PnlChartView({ columns, rows }) {
  if (!columns?.length || !rows?.length) return null;

  // Filter KPI rows
  const kpiRows = rows.filter(r => r.row_type === 'kpi' || r['row_type'] === 'kpi');
  if (kpiRows.length === 0) return null;

  // Value columns (exclude Total for chart — it's a sum, not a period)
  const valueCols = (columns || []).filter(c => !META_COLS.has(c) && c !== 'Total');
  if (valueCols.length === 0) return null;

  // Build series — one per KPI
  const series = kpiRows.map((kpi, idx) => {
    const label = kpi.label || kpi['label'] || `KPI ${idx + 1}`;
    const data = valueCols.map(col => {
      const val = kpi[col];
      return typeof val === 'number' ? val : Number(val) || 0;
    });
    const color = KPI_COLORS[label] || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];

    return {
      name: label,
      type: 'bar',
      data,
      itemStyle: { color, borderRadius: [2, 2, 0, 0] },
      emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.15)' } },
      barMaxWidth: 32,
    };
  });

  const option = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      valueFormatter: (val) => {
        if (val === null || val === undefined) return '';
        const num = Number(val);
        const abs = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return num < 0 ? `-$${abs}` : `$${abs}`;
      },
    },
    legend: {
      bottom: 0,
      left: 'center',
      textStyle: { color: '#71717a', fontSize: 11 },
    },
    grid: {
      containLabel: true,
      left: '3%',
      right: '4%',
      top: 20,
      bottom: 50,
    },
    xAxis: {
      type: 'category',
      data: valueCols,
      axisLabel: { color: '#71717a', fontSize: 10, rotate: valueCols.length > 6 ? 30 : 0 },
      axisLine: { lineStyle: { color: '#e4e4e7' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#71717a',
        fontSize: 10,
        formatter: (val) => {
          if (Math.abs(val) >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
          if (Math.abs(val) >= 1000) return `$${(val / 1000).toFixed(0)}K`;
          return `$${val}`;
        },
      },
      splitLine: { lineStyle: { color: '#f4f4f5' } },
    },
    series,
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
      <ReactECharts
        option={option}
        style={{ height: '280px', width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
    </div>
  );
}
